import React from "react";
import { IAddProductInput, ICategory } from "muminsmart-utils";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dropzone } from "@/components/ui/dropzone";
import { FieldGroup, FieldLabel, FieldSet } from "@/components/ui/field";
import {
  countMatrixCombinations,
  createRandomSku,
  generateVariantsFromMatrix,
  getEffectiveAttributeValues,
  getMatrixAttributes,
  parseAttributeValues,
  useCategoryVariantAttributes,
} from "@/app/components/product/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

interface VariantMatrixGeneratorProps {
  categories: ICategory[];
  selectedSubSubCategoryIds: string[];
  productAlwaysAvailable: boolean;
}

const VariantMatrixGenerator: React.FC<VariantMatrixGeneratorProps> = ({
  categories,
  selectedSubSubCategoryIds,
  productAlwaysAvailable,
}) => {
  const form = useFormContext<IAddProductInput>();
  const watchedVariants = useWatch({
    control: form.control,
    name: "variants",
  });

  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = React.useState(false);
  const [pendingCombinationCount, setPendingCombinationCount] = React.useState(0);

  const { variantAttributes } = useCategoryVariantAttributes(
    categories,
    selectedSubSubCategoryIds,
  );

  const matrixAttributes = React.useMemo(
    () => getMatrixAttributes(variantAttributes),
    [variantAttributes],
  );

  const [attributeValues, setAttributeValues] = React.useState<
    Record<string, string[]>
  >({});

  const [valueInputs, setValueInputs] = React.useState<Record<string, string>>(
    {},
  );

  const [defaults, setDefaults] = React.useState<{
    price: number;
    discountedPrice: number;
    weight: number;
    stock: number;
    featuredImage?: File | string;
    additionalImages: Array<File | string>;
    bulkPrices: Array<{ quantity: number; price: number }>;
  }>({
    price: 0,
    discountedPrice: 0,
    weight: 0,
    stock: 0,
    additionalImages: [],
    bulkPrices: [],
  });

  React.useEffect(() => {
    const existingVariants = form.getValues("variants") ?? [];
    const firstVariant = existingVariants[0];

    setAttributeValues((current) => {
      const next: Record<string, string[]> = {};

      for (const { attribute } of variantAttributes) {
        const existingValues = current[attribute.id] ?? [];
        const valuesFromVariants = new Set(existingValues);

        for (const variant of existingVariants) {
          const matchedAttribute = variant.variantAttributes?.find(
            (item) => item.attributeId === attribute.id,
          );

          if (matchedAttribute?.value) {
            valuesFromVariants.add(matchedAttribute.value);
          }
        }

        next[attribute.id] = [...valuesFromVariants];
      }

      return next;
    });

    setValueInputs((current) => {
      const next: Record<string, string> = {};

      for (const { attribute } of variantAttributes) {
        next[attribute.id] = current[attribute.id] ?? "";
      }

      return next;
    });

    if (firstVariant) {
      setDefaults((current) => ({
        price: current.price || Number(firstVariant.price ?? 0),
        discountedPrice:
          current.discountedPrice || Number(firstVariant.discountedPrice ?? 0),
        weight: current.weight || Number(firstVariant.weight ?? 0),
        stock: current.stock || Number(firstVariant.stock ?? 0),
        featuredImage: current.featuredImage ?? firstVariant.featuredImage,
        additionalImages: current.additionalImages.length
          ? current.additionalImages
          : [...(firstVariant.additionalImages ?? [])],
        bulkPrices: current.bulkPrices.length
          ? current.bulkPrices
          : (firstVariant.bulkPrices ?? []).map((bulkPrice) => ({
              quantity: Number(bulkPrice.quantity ?? 1),
              price: Number(bulkPrice.price ?? 0),
            })),
      }));
    }
  }, [form, variantAttributes]);

  React.useEffect(() => {
    if ((watchedVariants?.length ?? 0) > 0) {
      setIsCollapsed(true);
    }
  }, [watchedVariants?.length]);

  const effectiveAttributeValues = React.useMemo(
    () =>
      getEffectiveAttributeValues(
        attributeValues,
        valueInputs,
        matrixAttributes,
      ),
    [attributeValues, matrixAttributes, valueInputs],
  );

  const combinationCount = React.useMemo(
    () => countMatrixCombinations(effectiveAttributeValues, matrixAttributes),
    [effectiveAttributeValues, matrixAttributes],
  );

  const addAttributeValues = React.useCallback(
    (attributeId: string, rawInput: string) => {
      const parsedValues = parseAttributeValues(rawInput);

      if (!parsedValues.length) {
        return;
      }

      setAttributeValues((current) => ({
        ...current,
        [attributeId]: [
          ...new Set([...(current[attributeId] ?? []), ...parsedValues]),
        ],
      }));
      setValueInputs((current) => ({
        ...current,
        [attributeId]: "",
      }));
    },
    [],
  );

  const removeAttributeValue = React.useCallback(
    (attributeId: string, value: string) => {
      setAttributeValues((current) => ({
        ...current,
        [attributeId]: (current[attributeId] ?? []).filter(
          (item) => item !== value,
        ),
      }));
    },
    [],
  );

  const handleAddBulkPrice = React.useCallback(() => {
    setDefaults((current) => ({
      ...current,
      bulkPrices: [...current.bulkPrices, { quantity: 1, price: 0 }],
    }));
  }, []);

  const handleDeleteBulkPrice = React.useCallback((index: number) => {
    setDefaults((current) => ({
      ...current,
      bulkPrices: current.bulkPrices.filter(
        (_, bulkIndex) => bulkIndex !== index,
      ),
    }));
  }, []);

  const handleUpdateBulkPrice = React.useCallback(
    (index: number, field: "quantity" | "price", value: number) => {
      setDefaults((current) => ({
        ...current,
        bulkPrices: current.bulkPrices.map((bulkPrice, bulkIndex) =>
          bulkIndex === index ? { ...bulkPrice, [field]: value } : bulkPrice,
        ),
      }));
    },
    [],
  );

  const executeGenerateVariants = React.useCallback(
    (resolvedAttributeValues: Record<string, string[]>) => {
      const generatedVariants = generateVariantsFromMatrix({
        attributeValues: resolvedAttributeValues,
        matrixAttributes,
        allVariantAttributes: variantAttributes,
        existingVariants: form.getValues("variants") ?? [],
        defaults: {
          ...defaults,
          alwaysAvailable: productAlwaysAvailable,
        },
        createSku: createRandomSku,
      });

      form.setValue("variants", generatedVariants, {
        shouldDirty: true,
        shouldTouch: true,
      });

      setIsCollapsed(true);
      toast.success(`Generated ${generatedVariants.length} variants`);
    },
    [
      defaults,
      form,
      matrixAttributes,
      productAlwaysAvailable,
      variantAttributes,
    ],
  );

  const handleGenerateVariants = React.useCallback(() => {
    if (!matrixAttributes.length) {
      return;
    }

    const resolvedAttributeValues = getEffectiveAttributeValues(
      attributeValues,
      valueInputs,
      matrixAttributes,
    );

    setAttributeValues(resolvedAttributeValues);
    setValueInputs((current) => {
      const next: Record<string, string> = {};

      for (const attribute of matrixAttributes) {
        next[attribute.attributeId] = "";
      }

      return { ...current, ...next };
    });

    const resolvedCombinationCount = countMatrixCombinations(
      resolvedAttributeValues,
      matrixAttributes,
    );

    const missingAttributes = matrixAttributes.filter(
      (attribute) =>
        !(resolvedAttributeValues[attribute.attributeId]?.length ?? 0),
    );

    if (missingAttributes.length > 0) {
      toast.error(
        `Add at least one value for: ${missingAttributes.map((item) => item.attribute.name).join(", ")}`,
      );
      return;
    }

    if (resolvedCombinationCount === 0) {
      toast.error("Add values for each attribute to generate variants");
      return;
    }

    if (!defaults.featuredImage) {
      toast.error("Featured image is required for generated variants");
      return;
    }

    if (defaults.additionalImages.length === 0) {
      toast.error("At least one additional image is required");
      return;
    }

    const existingVariants = form.getValues("variants") ?? [];

    if (existingVariants.length > 0) {
      setPendingCombinationCount(resolvedCombinationCount);
      setShowReplaceConfirm(true);
      return;
    }

    executeGenerateVariants(resolvedAttributeValues);
  }, [
    attributeValues,
    defaults,
    executeGenerateVariants,
    form,
    matrixAttributes,
    valueInputs,
  ]);

  const handleConfirmReplace = React.useCallback(() => {
    const resolvedAttributeValues = getEffectiveAttributeValues(
      attributeValues,
      valueInputs,
      matrixAttributes,
    );

    setAttributeValues(resolvedAttributeValues);
    setValueInputs((current) => {
      const next: Record<string, string> = {};

      for (const attribute of matrixAttributes) {
        next[attribute.attributeId] = "";
      }

      return { ...current, ...next };
    });

    executeGenerateVariants(resolvedAttributeValues);
    setShowReplaceConfirm(false);
  }, [
    attributeValues,
    executeGenerateVariants,
    matrixAttributes,
    valueInputs,
  ]);

  if (!matrixAttributes.length) {
    return null;
  }

  return (
    <>
      <section className="mb-8 w-full rounded-3xl border border-primary/20 bg-amber-50/40 p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-[#2f2a27]">
              Generate Variants
            </h2>
            <p className="text-sm text-muted-foreground">
              Enter all possible values for each attribute. Every combination
              will become a variant with the default price, stock, bulk prices,
              and images below.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsCollapsed((current) => !current)}
            className="inline-flex min-h-11 w-full items-center justify-center gap-1 rounded-full border border-primary/30 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 sm:min-h-0 sm:w-auto sm:px-3 sm:py-1.5 sm:text-xs"
          >
            {isCollapsed ? "Expand" : "Collapse"}
            {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>

        {!isCollapsed ? (
          <div className="space-y-5">
        {matrixAttributes.map((matrixAttribute) => {
          const values = attributeValues[matrixAttribute.attributeId] ?? [];
          const inputValue = valueInputs[matrixAttribute.attributeId] ?? "";

          return (
            <div
              key={matrixAttribute.attributeId}
              className="rounded-2xl border border-muted bg-white p-4"
            >
              <Label className="mb-3 block font-medium">
                {matrixAttribute.attribute.name}
              </Label>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={inputValue}
                  placeholder="e.g. A, B, C"
                  className="rounded-3xl border-muted bg-white p-3"
                  onChange={(event) =>
                    setValueInputs((current) => ({
                      ...current,
                      [matrixAttribute.attributeId]: event.target.value,
                    }))
                  }
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") {
                      return;
                    }

                    event.preventDefault();
                    addAttributeValues(matrixAttribute.attributeId, inputValue);
                  }}
                />
                <button
                  type="button"
                  onClick={() =>
                    addAttributeValues(matrixAttribute.attributeId, inputValue)
                  }
                  className="min-h-11 w-full shrink-0 rounded-full border border-primary/30 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 sm:min-h-0 sm:w-auto sm:py-2"
                >
                  Add Values
                </button>
              </div>

              <p className="mt-2 text-xs text-muted-foreground">
                Separate multiple values with commas. Press Enter or click Add
                Values.
              </p>

              {values.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {values.map((value) => (
                    <span
                      key={value}
                      className="inline-flex items-center gap-1 rounded-full border border-muted bg-amber-50/60 px-3 py-1 text-sm"
                    >
                      {value}
                      <button
                        type="button"
                        onClick={() =>
                          removeAttributeValue(
                            matrixAttribute.attributeId,
                            value,
                          )
                        }
                        aria-label={`Remove ${value}`}
                        className="rounded-full p-0.5 text-muted-foreground hover:bg-white hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="matrix-default-price">Default Price</Label>
            <Input
              id="matrix-default-price"
              type="number"
              min="0"
              value={defaults.price}
              onChange={(event) =>
                setDefaults((current) => ({
                  ...current,
                  price: Number(event.target.value) || 0,
                }))
              }
              className="rounded-3xl border-muted bg-white p-3"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="matrix-default-discounted-price">
              Default Discounted Price
            </Label>
            <Input
              id="matrix-default-discounted-price"
              type="number"
              min="0"
              value={defaults.discountedPrice}
              onChange={(event) =>
                setDefaults((current) => ({
                  ...current,
                  discountedPrice: Number(event.target.value) || 0,
                }))
              }
              className="rounded-3xl border-muted bg-white p-3"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="matrix-default-weight">Default Weight (kgs)</Label>
            <Input
              id="matrix-default-weight"
              type="number"
              min="0"
              step="0.1"
              max="50"
              value={defaults.weight}
              onChange={(event) =>
                setDefaults((current) => ({
                  ...current,
                  weight: Number(event.target.value) || 0,
                }))
              }
              className="rounded-3xl border-muted bg-white p-3"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="matrix-default-stock">Default Stock</Label>
            <Input
              id="matrix-default-stock"
              type="number"
              min="0"
              value={defaults.stock}
              onChange={(event) =>
                setDefaults((current) => ({
                  ...current,
                  stock: Number(event.target.value) || 0,
                }))
              }
              className="rounded-3xl border-muted bg-white p-3"
            />
          </div>
        </div>

        <FieldGroup className="gap-2">
          <FieldSet>
            <FieldLabel>Default Bulk Prices</FieldLabel>
          </FieldSet>

          {defaults.bulkPrices.map((bulkPrice, bulkPriceIndex) => (
            <div
              key={`matrix-bulk-${bulkPriceIndex}`}
              className="rounded-3xl border border-muted bg-white p-3"
            >
              <div className="mb-2 flex w-full items-center justify-between gap-2 border-b pb-2 text-sm">
                <p className="font-medium">Bulk Price {bulkPriceIndex + 1}</p>
                <button
                  type="button"
                  onClick={() => handleDeleteBulkPrice(bulkPriceIndex)}
                  aria-label={`Remove bulk price ${bulkPriceIndex + 1}`}
                  className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`matrix-bulk-qty-${bulkPriceIndex}`}>
                    Qty
                  </Label>
                  <Input
                    id={`matrix-bulk-qty-${bulkPriceIndex}`}
                    type="number"
                    min="1"
                    value={bulkPrice.quantity}
                    onChange={(event) =>
                      handleUpdateBulkPrice(
                        bulkPriceIndex,
                        "quantity",
                        Number(event.target.value) || 1,
                      )
                    }
                    className="rounded-3xl border-muted bg-white p-3"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`matrix-bulk-price-${bulkPriceIndex}`}>
                    Price
                  </Label>
                  <Input
                    id={`matrix-bulk-price-${bulkPriceIndex}`}
                    type="number"
                    min="0"
                    value={bulkPrice.price}
                    onChange={(event) =>
                      handleUpdateBulkPrice(
                        bulkPriceIndex,
                        "price",
                        Number(event.target.value) || 0,
                      )
                    }
                    className="rounded-3xl border-muted bg-white p-3"
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex items-center justify-start">
            <button
              type="button"
              onClick={handleAddBulkPrice}
              className="rounded-md border border-primary/30 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5"
            >
              + Add Bulk Price
            </button>
          </div>
        </FieldGroup>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <Label>Default Featured Image</Label>
            <Dropzone
              label=""
              className="w-full"
              accept={{ "image/*": [] }}
              maxFiles={1}
              value={defaults.featuredImage ? [defaults.featuredImage] : []}
              onFilesChange={(files) =>
                setDefaults((current) => ({
                  ...current,
                  featuredImage: files[0],
                }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>Default Additional Images</Label>
            <Dropzone
              label=""
              className="w-full"
              accept={{ "image/*": [] }}
              maxFiles={10}
              value={defaults.additionalImages}
              onFilesChange={(files) =>
                setDefaults((current) => ({
                  ...current,
                  additionalImages: files,
                }))
              }
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {combinationCount > 0
              ? `${combinationCount} variant${combinationCount === 1 ? "" : "s"} will be created`
              : "Add values for each attribute to preview the variant count"}
          </p>

          <button
            type="button"
            onClick={handleGenerateVariants}
            className="min-h-12 w-full rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 sm:min-h-0 sm:w-auto sm:py-2.5"
          >
            Generate Variants
          </button>
        </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Generator collapsed.
            {combinationCount > 0
              ? ` ${combinationCount} combination${combinationCount === 1 ? "" : "s"} ready to generate.`
              : " Expand to add attribute values and defaults."}
            {(watchedVariants?.length ?? 0) > 0
              ? ` ${watchedVariants?.length ?? 0} variants currently in the form.`
              : ""}
          </p>
        )}
      </section>

      <Dialog open={showReplaceConfirm} onOpenChange={setShowReplaceConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace existing variants?</DialogTitle>
            <DialogDescription>
              This will regenerate {pendingCombinationCount} variants from your
              current attribute selections. Existing variants with the same
              attribute combination will keep their SKU and data, but the full
              variant list will be rebuilt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowReplaceConfirm(false)}
              className="min-h-11 w-full sm:min-h-0 sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmReplace}
              className="min-h-11 w-full sm:min-h-0 sm:w-auto"
            >
              Replace Variants
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VariantMatrixGenerator;
